import React from 'react'
import PropTypes from 'prop-types'
import { commodityCodePath } from 'components/routes/api'
import { api } from 'components/api'
import CommodityClass, { Commodity } from './CommodityClass'
import CheckboxPartialSelected from 'images/supplier_commodity_picker/checkbox-partial-selected.svg'
import CheckboxSelected from 'images/supplier_commodity_picker/checkbox-selected.svg'
import CheckboxUnchecked from 'images/supplier_commodity_picker/checkbox-unchecked.svg'
import CollapseSmall from 'images/supplier_commodity_picker/collapse-small.svg'
import ExpandSmall from 'images/supplier_commodity_picker/expand-small.svg'
import Focusable from './Focusable'

export interface Family {
  id: string | number
  code: string
  all_classes_selected: boolean
  commodities: Commodity[]
  item_description: string
}

export interface FamilyComponentProps {
  family: Family
  expanded: boolean
  selectedCommodities?: Commodity[]
  setExpandedFamilyCode: (code: string | null) => void
  setActiveCode: (id: string | null) => void
  isActiveCode: (props: { id: string }) => boolean
  isInActiveCodeHierarchy: (props: { id: string }) => boolean
  onSelect: (family: Family, shouldRemove: boolean) => void
}

interface FamilyComponentState {
  commodities: Commodity[]
}

class FamilyComponent extends React.Component<FamilyComponentProps, FamilyComponentState> {
  constructor (props: FamilyComponentProps) {
    super(props)

    this.state = {
      commodities: []
    }
  }

  family = (): Family => this.props.family
  isExpanded = (): boolean => this.props.expanded
  selectedCommodities = (): Commodity[] | undefined => this.props.selectedCommodities
  loadedCommodities = (): Commodity[] => this.state.commodities

  handleToggleFamily = (): void => {
    this.isExpanded() ? this.collapse() : this.expand()
  }

  expand = (): void => {
    this.lazyLoadCommodityClasses().catch(e => console.error(e))
    if (!this.isExpanded()) {
      this.props.setExpandedFamilyCode(this.family().code)
    }
  }

  collapse = (): void => {
    this.props.setExpandedFamilyCode(null)
    this.props.setActiveCode(null)
  }

  lazyLoadCommodityClasses = async (): Promise<void> => {
    if (this.loadedCommodities().length > 0) { return }

    const response = await api.get<unknown, Commodity[]>({
      url: commodityCodePath(this.family().id),
      headers: {
        Accept: 'application/json'
      }
    })

    // Wait for setState to complete before resolving
    return await new Promise<void>((resolve) => {
      this.setState({
        commodities: response.data
      }, () => {
        // setState callback
        // runs after setState completes, and will rerender the component with the updated state
        resolve()
      })
    })
  }

  descriptionId = (): string => {
    return `code-${this.family().code}-description`
  }

  isSelected = (): boolean => {
    return this.family().all_classes_selected
  }

  isPartiallySelected = (): boolean => {
    const selectedCommodities = this.selectedCommodities()
    return !this.family().all_classes_selected && selectedCommodities !== undefined && selectedCommodities.length > 0
  }

  ariaChecked = (): boolean | 'mixed' => {
    if (this.isSelected()) {
      return true
    } else if (this.isPartiallySelected()) {
      return 'mixed'
    } else {
      return false
    }
  }

  renderCheckboxImage = (): React.ReactElement => {
    if (this.isSelected()) {
      return <img src={CheckboxSelected} />
    } else if (this.isPartiallySelected()) {
      return <img src={CheckboxPartialSelected} />
    } else {
      return <img src={CheckboxUnchecked} />
    }
  }

  handleSelect = (commodity: Commodity): void => {
    const updatedFamily = this.family()
    const selectedCommodities = this.selectedCommodities()

    if (selectedCommodities?.some(selectedCommodity => selectedCommodity.id === commodity.id) === true) {
      updatedFamily.commodities = selectedCommodities.filter(selectedCommodity => selectedCommodity.id !== commodity.id)
    } else if (selectedCommodities !== undefined && selectedCommodities.length > 0) {
      updatedFamily.commodities = [...updatedFamily.commodities, commodity]
    } else {
      updatedFamily.commodities = [commodity]
    }

    updatedFamily.all_classes_selected = updatedFamily.commodities.length === this.loadedCommodities().length

    this.props.onSelect(updatedFamily, this.shouldRemove(updatedFamily))
  }

  handleFamilySelection = (): void => {
    this.lazyLoadCommodityClasses().then(() => {
      this.setFamilySelection(this.family(), this.loadedCommodities())
    }).catch(e => console.error(e))
  }

  setFamilySelection = (updatedFamily: Family, loadedCommodities: Commodity[]): void => {
    const selectedCommodities = this.selectedCommodities()
    if (selectedCommodities !== undefined && selectedCommodities.length === loadedCommodities.length) {
      updatedFamily.commodities = []
      updatedFamily.all_classes_selected = false
    } else {
      updatedFamily.commodities = loadedCommodities
      updatedFamily.all_classes_selected = true
    }

    this.props.onSelect(updatedFamily, this.shouldRemove(updatedFamily))
  }

  commodityCheckStatus = (commodity: Commodity): string => {
    const selectedCommodities = this.selectedCommodities()
    if (selectedCommodities?.some(selectedCommodity => selectedCommodity.id === commodity.id) === true) {
      return 'checked'
    } else {
      return 'unchecked'
    }
  }

  shouldRemove = (updatedFamily: Family): boolean => {
    return updatedFamily.commodities.length === 0
  }

  render (): React.ReactElement {
    return (
      <Focusable
        clearFocus={() => this.props.setActiveCode(null)}
        isActiveItem={this.props.isActiveCode}
        isInActiveItemHierarchy={this.props.isInActiveCodeHierarchy}
        itemId={this.family().id.toString()}
        expand={this.expand}
      >
        {({ Checkbox, Highlightable }) => {
          return (
            <div className='commodity-code-selector-item family' data-testid={`code-${this.family().code}`}>
              <Highlightable>
                <div className='grid-x grid-margin-x align-middle'>
                  <div className='shrink cell'>
                    <Checkbox
                      handleSelect={this.handleFamilySelection} /* eslint-disable-line react/jsx-handler-names */
                      attributes={{
                        'aria-labelledby': this.descriptionId(),
                        'data-testid': `code-${this.family().code}-checkbox`,
                        'aria-checked': this.ariaChecked()
                      }}
                    >
                      {this.renderCheckboxImage()}
                    </Checkbox>
                  </div>
                  <div className='shrink cell'>
                    <div className='code'>FAMILY {this.props.family.code}</div>
                  </div>
                  <div className='description auto cell'>
                    {this.props.family.item_description}
                  </div>
                  <div className='expand-toggle shrink cell' onClick={this.handleToggleFamily}>
                    <button onClick={this.handleToggleFamily}>
                      {this.isExpanded() ? <img src={CollapseSmall} alt='Collapse' /> : <img src={ExpandSmall} alt='Expand' />}
                    </button>
                  </div>
                </div>
              </Highlightable>
              {this.isExpanded()
                ? this.loadedCommodities().map(commodity =>
                  <CommodityClass
                    commodity={commodity}
                    key={commodity.code}
                    onSelect={this.handleSelect}
                    checkStatus={this.commodityCheckStatus(commodity)}
                    setActiveCode={this.props.setActiveCode}
                    isActiveCode={this.props.isActiveCode}
                    isInActiveCodeHierarchy={this.props.isInActiveCodeHierarchy}
                  />
                )
                : null}
            </div>
          )
        }}
      </Focusable>
    )
  }
}

(FamilyComponent as any).propTypes = {
  family: PropTypes.object.isRequired,
  expanded: PropTypes.bool.isRequired,
  selectedCommodities: PropTypes.array,
  onSelect: PropTypes.func.isRequired,
  setActiveCode: PropTypes.func.isRequired,
  isActiveCode: PropTypes.func.isRequired,
  isInActiveCodeHierarchy: PropTypes.func.isRequired,
  setExpandedFamilyCode: PropTypes.func.isRequired
}

export default FamilyComponent
